import { DSDropdownMenuT } from '@elliemae/ds-dropdownmenu-v2';
import { IField, IScreen, SchemaRule } from '@elliemae/ns-schema';
import {
  DraggableField,
  FieldControlPath,
  PricingSearchConfig,
  RequiredSchemaField,
  SchemaAndRules,
  YupIField,
  YupSchemaAndRules,
  getAllDraggableFields,
} from '@frontend/pricing-search';
import { UseFormGetValues } from 'react-hook-form';
import { create } from 'zustand';
import { EmptySchema } from '../constants/config';
import { ChannelOptions } from '../constants/index';
import { debugValues } from './debug-values';
import { getAllLookups } from '../../../api/eppsService';

export interface AppStore {
  isFullScreenLoadingSpinnerVisible: boolean;
  fullScreenLoadingSpinnerText: string;
  isDebugMode: boolean;
  useLocalTestData: boolean;
  chokeConsoleWarnings: boolean;
  toggleFullScreenLoadingSpinnerVisibility: (
    isVisible: boolean,
    text?: string,
  ) => void;
  clientId: number;
}
/**
 * Application Store
 */
export const useAppStore = create<AppStore>((set) => ({
  ...debugValues,
  isFullScreenLoadingSpinnerVisible: true,
  fullScreenLoadingSpinnerText: 'Loading Configuration...',
  toggleFullScreenLoadingSpinnerVisibility: (isVisible: boolean, text = '') =>
    set(() => ({
      isFullScreenLoadingSpinnerVisible: isVisible,
      fullScreenLoadingSpinnerText: text,
    })),
  clientId: 1,
}));

export interface FormActions {
  saveCounter: number;
  saveSchemaToDraft: () => void;
  promoteCounter: number;
  promoteSchemaToPublished: () => void;
  resetCounter: number;
  resetFormToDefault: () => void;
  resetAllActionCounters: () => void;
  missingRequiredFields: RequiredSchemaField[];
  setMissingRequiredFields: (fields: RequiredSchemaField[]) => void;
}

export const useFormActions = create<FormActions>((set, get) => ({
  saveCounter: 0,
  saveSchemaToDraft: () => {
    set(() => ({ saveCounter: get().saveCounter + 1 }));
  },
  promoteCounter: 0,
  promoteSchemaToPublished: () => {
    set(() => ({
      promoteCounter: get().promoteCounter + 1,
    }));
  },
  resetCounter: 0,
  resetFormToDefault: () => {
    set(() => ({
      resetCounter: get().resetCounter + 1,
    }));
  },
  resetAllActionCounters: () => {
    set(() => ({
      saveCounter: 0,
      promoteCounter: 0,
      resetCounter: 0,
    }));
  },
  missingRequiredFields: [],
  setMissingRequiredFields: (missingRequiredFields: RequiredSchemaField[]) => {
    set(() => ({ missingRequiredFields }));
  },
}));

export interface SchemaStore {
  clientConfigs: PricingSearchConfig[];
  setClientConfigs: (configs: PricingSearchConfig[]) => void;
  selectedChannel: DSDropdownMenuT.ItemSingleOptions;
  setSelectedChannel: (channel: DSDropdownMenuT.ItemSingleOptions) => void;
  fieldList: DraggableField[];
  removeFieldFromList: (fieldId?: string) => void;
  addFieldsToList: (field: YupIField[]) => void;
  setFieldList: (fields: DraggableField[]) => void;
  allCustomFields: DraggableField[];
  allStandardFields: DraggableField[];
  setAllStandardFields: (fields: DraggableField[]) => void;
  setAllCustomFields: (fields: DraggableField[]) => void;
  defaultSchemaWithCustomFields: SchemaAndRules | null;
  setDefaultSchemaWithCustomFields: (schema: SchemaAndRules) => void;
  clientLookups: Awaited<ReturnType<typeof getAllLookups>> | null;
  setClientLookups: (
    lookups: Awaited<ReturnType<typeof getAllLookups>>,
  ) => void;
  workingSchema: SchemaAndRules;
  setWorkingSchema: (schema: SchemaAndRules) => void;
  getFormValues:
    | ((path?: string) => UseFormGetValues<YupSchemaAndRules>)
    | null;
  setFormValueGetter: (
    getFormValueMethod: (path?: string) => UseFormGetValues<YupSchemaAndRules>,
  ) => void;
  selectedSchemaElement: FieldControlPath | null;
  setSelectedSchemaElement: (selectedElement: FieldControlPath | null) => void;
  clearSelectedSchemaElement: () => void;
  isPropertyPanelOpen: boolean;
  setIsPropertyPanelOpen: (open: boolean) => void;
  selectedRule: { rule: SchemaRule } | null;
  setSelectedRule: (options: { rule: SchemaRule } | null) => void;
}
/**
 * Schema UI Store
 */
export const useSchemaStore = create<SchemaStore>((set, get) => ({
  clientConfigs: [],
  setClientConfigs(configs: PricingSearchConfig[]) {
    set(() => ({
      clientConfigs: configs,
    }));
  },
  selectedChannel: ChannelOptions[0],
  setSelectedChannel: (channel: DSDropdownMenuT.ItemSingleOptions) => {
    const workingSchema =
      get().clientConfigs.find((i) => i.channel === channel.value)
        ?.draftSchema || EmptySchema;
    set(() => ({
      workingSchema,
      selectedChannel: channel,
      selectedSchemaElement: null,
    }));
  },
  fieldList: [],
  removeFieldFromList(fieldId?: string) {
    const fields = get().fieldList;
    set(() => ({
      fieldList: fields.filter((i) => i.schema?.component_id !== fieldId),
    }));
  },
  addFieldsToList(fields: YupIField[]) {
    const currenFieldlist = get().fieldList;
    const allcustomFields = get().allCustomFields;
    const fieldsToAdd = fields
      .map((field) => {
        const isCustom = field.component_id?.includes('CUSTOM');
        const matchedField = (
          isCustom ? allcustomFields : getAllDraggableFields()
        ).find(
          (i) => i.schema?.component_id === field.component_id,
        ) as DraggableField;
        return matchedField;
      })
      .filter(Boolean);
    set(() => ({
      fieldList: [...currenFieldlist, ...fieldsToAdd],
    }));
  },
  setFieldList(fieldList: DraggableField[]) {
    set(() => ({
      fieldList,
    }));
  },
  allCustomFields: [],
  allStandardFields: getAllDraggableFields(),
  setAllStandardFields(allStandardFields: DraggableField[]) {
    set(() => ({
      allStandardFields,
    }));
  },
  setAllCustomFields(allCustomFields: DraggableField[]) {
    set(() => ({
      allCustomFields,
    }));
  },
  defaultSchemaWithCustomFields: null,
  setDefaultSchemaWithCustomFields(
    defaultSchemaWithCustomFields: SchemaAndRules,
  ) {
    set(() => ({
      defaultSchemaWithCustomFields,
    }));
  },
  clientLookups: null,
  setClientLookups: (lookups: Awaited<ReturnType<typeof getAllLookups>>) => {
    set(() => ({
      clientLookups: lookups,
    }));
  },
  workingSchema: {
    schema: {
      modules: [],
      theme: {} as any,
      screens: [],
    },
    schemaRules: [],
  },
  setWorkingSchema(workingSchema: SchemaAndRules) {
    set(() => ({
      workingSchema: {
        ...(workingSchema ?? {}),
      },
    }));
  },
  getFormValues: null,
  setFormValueGetter(
    getFormValueMethod: (path?: string) => UseFormGetValues<YupSchemaAndRules>,
  ) {
    set(() => ({
      getFormValues: getFormValueMethod,
    }));
  },
  selectedSchemaElement: null, // this can be field, screen, module
  setSelectedSchemaElement: (selectedElementPath: FieldControlPath | null) => {
    if (!selectedElementPath) {
      set(() => ({ isPropertyPanelOpen: false }));
      return;
    }
    if (!get().isPropertyPanelOpen) {
      set(() => ({ isPropertyPanelOpen: true }));
    }
    if (selectedElementPath === get().selectedSchemaElement) {
      return;
    }
    set(() => ({
      selectedSchemaElement: selectedElementPath,
    }));
  },
  clearSelectedSchemaElement: () => {
    set(() => ({
      selectedSchemaElement: null,
    }));
  },
  isPropertyPanelOpen: false,
  setIsPropertyPanelOpen(open) {
    set(() => ({
      isPropertyPanelOpen: open,
    }));
  },
  selectedRule: null,
  setSelectedRule(rule) {
    set(() => ({
      selectedRule: rule,
    }));
  },
}));
